
import {
    Button,
    LinkIcon,
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    getKeyValue,
    Chip,
    Skeleton,
    Spacer,
    Tooltip,
    SelectItem,
    Select,
    Input,
    useDisclosure,
    Modal,
    ModalContent,
    ModalFooter,
    ModalBody,
    ModalHeader,
} from "@heroui/react";

import { Box } from "@mui/material";
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import { v4 as uuidv4 } from 'uuid';

import { JobListing, JobListingStatus } from "../../../types";
import { Column, Row } from "../../../components/Layout";
import TextHero from "../../../components/typography/TextHero";
import TextSubtitle from "../../../components/typography/TextSubtitle";
import InfoTooltip from "../../../components/InfoTooltip";
import { Key, useEffect, useState } from "react";
import TextLabel from "../../../components/typography/TextLabel";


export interface JobsTableEntry extends JobListing {
    status: JobListingStatus;
}

interface JobsTableParams {
    loading: boolean
    entries: JobsTableEntry[]
}

export interface JobTableFilterData {
    jobTitleQuery: string
    selectedCategories: string[]
    selectedLocations: string[]
    selectedStatuses: JobListingStatus[]
}

interface JobsTableFilterParams {
    loading?: boolean
    disabled?: boolean
    categoriesOptions: string[]
    locationsOptions: string[]
    onFiltersChanged: (data: JobTableFilterData) => void
    filterResetDeps?: React.DependencyList | undefined
}

export const initialJobTableFilterData: JobTableFilterData = {
    jobTitleQuery: "",
    selectedCategories: [],
    selectedLocations: [],
    selectedStatuses: [],
}


export function JobsTableFilters(params: JobsTableFilterParams) {
    const [filterData, setFilterData] = useState<JobTableFilterData>(initialJobTableFilterData)

    const { loading, disabled, categoriesOptions, locationsOptions, onFiltersChanged, filterResetDeps } = params;
    const { selectedCategories, selectedLocations, selectedStatuses, jobTitleQuery } = filterData

    function _setFilterData(data: JobTableFilterData) {
        onFiltersChanged(data)
        setFilterData(data)
    }

    function resetJobTitleFilter() {
        _setFilterData({ ...filterData, jobTitleQuery: "" });
    }

    function resetTypeFilter() {
        _setFilterData({ ...filterData, selectedStatuses: [] });
    }

    function resetLocationsFilter() {
        _setFilterData({ ...filterData, selectedLocations: [] });
    }

    function resetCategoriesFilter() {
        _setFilterData({ ...filterData, selectedCategories: [] });
    }

    function resetFilters() {
        _setFilterData(initialJobTableFilterData)
    };

    useEffect(() => { resetFilters(); }, filterResetDeps)

    const filtersApplied = selectedCategories.length > 0 || selectedLocations.length > 0 || selectedStatuses.length > 0 || jobTitleQuery

    return (
        <Box className='w-full'>
            <Row className="filters-reset" alignItems={"center"} mb={1} padding={1}>
                <TextLabel>Filters</TextLabel>
                {(<Button className={filtersApplied ? "" : "opacity-0"} disabled={!filtersApplied || loading} variant="light" onPress={resetFilters} endContent={filtersApplied ? <RestartAltIcon /> : <Spacer />}>
                    {filtersApplied ? "Reset Filters" : ""}
                </Button>)}
            </Row>

            <Row className="filters" mt={-3} py={1} px={1} gap={4}>
                <Column className="job-title w-4/12">
                    <Input
                        disabled={loading || (disabled && !jobTitleQuery)}
                        value={jobTitleQuery}
                        isClearable
                        onClear={resetJobTitleFilter}
                        size="sm"
                        labelPlacement="outside"
                        label="Job Title or URL"
                        variant="bordered"
                        onChange={
                            (e) => {
                                const _query = e.target.value
                                _setFilterData({ ...filterData, jobTitleQuery: _query })
                            }
                        }
                    />
                </Column>
                <Column className="job-category w-3/12">
                    <Select
                        endContent={
                            selectedCategories.length > 0 ? <RestartAltIcon onClick={resetCategoriesFilter} /> : ""
                        }
                        disabled={loading || disabled}
                        size="sm"
                        labelPlacement="outside"
                        label="Category"
                        variant="bordered"
                        selectedKeys={selectedCategories}
                        selectionMode="multiple"
                        onSelectionChange={
                            (keys) => {
                                const _selectedCategories = (Array.from(keys as Set<Key>).map(k => k.toString()))
                                _setFilterData({ ...filterData, selectedCategories: _selectedCategories })
                            }
                        }
                    >
                        {categoriesOptions.map(c => <SelectItem key={c}>{c}</SelectItem>)}
                    </Select>
                </Column>
                <Column className="job-location w-3/12">
                    <Select
                        endContent={
                            selectedLocations.length > 0 ? <RestartAltIcon onClick={resetLocationsFilter} /> : ""
                        }
                        disabled={loading || disabled}
                        size="sm"
                        labelPlacement="outside"
                        label="Location"
                        variant="bordered"
                        selectedKeys={selectedLocations}
                        selectionMode="multiple"
                        onSelectionChange={
                            (keys) => {
                                const _selectedLocations = (Array.from(keys as Set<Key>).map(k => k.toString()))
                                _setFilterData({ ...filterData, selectedLocations: _selectedLocations })
                            }
                        }
                    >
                        {locationsOptions.map(l => <SelectItem key={l}>{l}</SelectItem>)}
                    </Select>
                </Column>
                <Column className="job-advert-status w-2/12">
                    <Select
                        endContent={
                            selectedStatuses.length > 0 ? <RestartAltIcon onClick={resetTypeFilter} /> : ""
                        }
                        disabled={loading || disabled}
                        size="sm"
                        labelPlacement="outside"
                        label="Status"
                        variant="bordered"
                        selectedKeys={selectedStatuses}
                        selectionMode="multiple"
                        onSelectionChange={
                            (keys) => {
                                const _selectedStatuses = Array.from(keys as Set<Key>).map(k => k.toString() as JobListingStatus)

                                _setFilterData({ ...filterData, selectedStatuses: _selectedStatuses })
                            }
                        }
                    >
                        <SelectItem key="Open">Open</SelectItem>
                        <SelectItem key="New">New</SelectItem>
                        <SelectItem key="Closed">Closed</SelectItem>
                    </Select>
                </Column>
            </Row>
        </Box>
    )
}

export default function JobsTable({ entries, loading }: JobsTableParams) {
    const { isOpen, onOpen, onOpenChange } = useDisclosure();

    let itemKey = 0

    const [selectedJob, setSelectedJob] = useState<JobsTableEntry | null>(null);

    return (
        <Box className='w-full'>
            {(entries.length > 0) &&

                (<Table
                    classNames={{ base: `custom-jobs-table`, wrapper: `custom-jobs-table-wrapper max-h-[60vh] min-h-[60vh]` }}
                    hideHeader
                >
                    <TableHeader>
                        <TableColumn key="title"><Box padding={1}>Job Title</Box></TableColumn>
                        <TableColumn key="category">Category</TableColumn>
                        <TableColumn key="location">Location</TableColumn>
                        <TableColumn key="status">Status</TableColumn>
                    </TableHeader>
                    <TableBody items={entries}>
                        {(item) => {
                            itemKey += 1

                            const openJobUrl = () => window.open(item.url)
                            return (
                                <TableRow key={uuidv4()}>
                                    {(columnKey) => {

                                        const widths: Record<string, string> = {
                                            "title": "w-4/12",
                                            "category": "w-3/12",
                                            "location": "w-3/12",
                                            "status": "w-2/12",
                                        };

                                        const val = getKeyValue(item, columnKey)
                                        let content = !loading ? val : <Skeleton style={{ flexGrow: 1 }} isLoaded={false} className="rounded-lg">Loading...</Skeleton>;

                                        if (columnKey === "title") {
                                            content = (
                                                <Row justifyContent="space-between" marginBottom={3} >

                                                    <Column className={"job-title scale-on-hover"} onClick={() => {
                                                        openJobUrl()
                                                        // setSelectedJob(item)
                                                        // onOpen()
                                                    }}>
                                                        {content}
                                                        <Skeleton className="rounded-lg" isLoaded={!loading} ><TextSubtitle>{item.url}</TextSubtitle></Skeleton>
                                                    </Column>

                                                    <Button className="scale-on-hover-lg" size="sm" isIconOnly disabled={loading} onPress={openJobUrl}>
                                                        <LinkIcon />
                                                    </Button>
                                                </Row>
                                            );
                                        } else if (columnKey === "status") {
                                            const color = item.status === "Open" ? "default" : item.status === "New" ? "success" : "danger";
                                            content = (
                                                <Row alignItems={"center"}>
                                                    <Button
                                                        radius="full"
                                                        onPress={openJobUrl}
                                                        size="sm"
                                                        className="scale-on-hover-lg"
                                                        color={color}>{!loading ? (val) : <Spacer x={4} />}
                                                    </Button>
                                                    {
                                                        (item.status == "Closed") &&
                                                        <InfoTooltip content={
                                                            <>
                                                                <div>
                                                                    A job listing with a <Button
                                                                        style={{ transform: "scale(0.8)" }}
                                                                        radius="full"
                                                                        size="sm"
                                                                        color={color}>{val}
                                                                    </Button> status only means that the job is not listed in the company's careers page.
                                                                </div>
                                                                <div>
                                                                    The webpage of this job may still be accessible.
                                                                </div>
                                                            </>
                                                        } />
                                                    }
                                                </Row>
                                            );
                                        }

                                        return (
                                            <TableCell className={`align-top column-${columnKey} ${widths[columnKey]}`}>
                                                {content}
                                            </TableCell>
                                        );
                                    }}
                                </TableRow>
                            )
                        }}
                    </TableBody>
                </Table >)
            }

            {
                (entries.length == 0) && (
                    <Box alignSelf={"center"} textAlign={"center"} mt={2} mx={"auto"}>
                        <TextHero>No jobs found.</TextHero>
                    </Box>
                )
            }

            {
                (
                    <Modal
                        className="dark" isOpen={isOpen}
                        onOpenChange={onOpenChange}
                        size="full"
                        backdrop="blur"

                    >
                        <ModalContent>
                            {(onClose) => (
                                <>
                                    <ModalHeader className="flex flex-col gap-1 ">{selectedJob?.title}</ModalHeader>
                                    {/* <ModalBody>
                                   
                                    </ModalBody> */}
                                    <Row expand>
                                        <Column expand>
                                            <iframe style={{ height: "100%" }} src={selectedJob?.url} />
                                        </Column>

                                        <Column color={"white"} width={"25%"} height={"100%"}>
                                            <Column expand>
                                                <TextLabel>Job Title</TextLabel>
                                                <TextHero>{selectedJob?.title}</TextHero>
                                                <TextLabel>Location</TextLabel>
                                                <div>{selectedJob?.location}</div>
                                                <TextLabel>Advert Bullet 1</TextLabel>
                                                <Input></Input>
                                                <TextLabel>Advert Bullet 2</TextLabel>
                                                <Input></Input>
                                                <TextLabel>Advert Bullet 3</TextLabel>
                                                <Input></Input>
                                            </Column>
                                            <Row>
                                                <Column>
                                                    <Button variant="flat" onPress={onClose}>
                                                        Get Job Details With ChatGPT
                                                    </Button>
                                                </Column>
                                                <Column>
                                                    <Button variant="flat" onPress={onClose}>
                                                        Add Job To Marlean
                                                    </Button>
                                                </Column>
                                            </Row>
                                        </Column>
                                    </Row>
                                    <ModalFooter>
                                        <Button color="danger" variant="light" onPress={onClose}>
                                            Close
                                        </Button>
                                        <Button variant="flat" onPress={onClose}>
                                            Action
                                        </Button>
                                    </ModalFooter>
                                </>
                            )}
                        </ModalContent>
                    </Modal>
                )
            }
        </Box >
    );
}

